import React, { useCallback, useEffect, useState } from 'react';
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaShieldAlt, FaTachometerAlt } from 'react-icons/fa';
import { useAuth } from '../auth/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useVehicle } from '../contexts/VehicleContext';
import { bodyListingService, listingService, usedListingService } from '../services/apiService';
import { getGradeMeta } from '../utils/usedPartGrades';
import { ProductDetailSkeleton } from './Skeleton';
import FitmentStrip from './partDetail/FitmentStrip';
import PriceBreakdownCard from './partDetail/PriceBreakdownCard';
import SellerBlock from './partDetail/SellerBlock';
import UsedPhotoGallery from './partDetail/used/UsedPhotoGallery';
import ColorMatchSection from './partDetail/body/ColorMatchSection';
import PanelDamageMap from './partDetail/body/PanelDamageMap';
import BodyAlternativesSection from './partDetail/body/BodyAlternativesSection';
import BodyCtaSection from './partDetail/body/BodyCtaSection';
import DispatchPhotoSection from './partDetail/body/DispatchPhotoSection';
import UsedVerificationPhotoSection from './partDetail/used/UsedVerificationPhotoSection';
import CrossSellSection from './partDetail/CrossSellSection';
import { useCrossSell } from '../hooks/useCrossSell';
import { useThemeClasses } from '../hooks/useThemeClasses';

function ConfirmModal({ title, body, confirmLabel, onConfirm, onCancel, loading }) {
  return (
    <div className="fixed inset-0 z-[125] flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-600 bg-[#0f172a] p-6 shadow-2xl">
        <h3 className="text-lg font-bold text-white">{title}</h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-300">{body}</p>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-slate-600 px-5 py-2.5 text-sm font-semibold text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-bold text-slate-900 hover:bg-amber-400 disabled:opacity-50"
          >
            {loading ? 'Please wait…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * B-06 — Part Detail (Body Parts — Color Match)
 */
export default function PartDetailBody({ listingIdOverride } = {}) {
  const { itemId } = useParams();
  const listingId = listingIdOverride || itemId;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { refreshCart } = useCart();
  const { selectedVehicle } = useVehicle();
  const { isDark, sc } = useThemeClasses();

  const resolvedVehicleId =
    searchParams.get('vehicleId') ||
    selectedVehicle?._id ||
    selectedVehicle?.id ||
    null;

  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState(null);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [showCheckModal, setShowCheckModal] = useState(false);
  const [approving, setApproving] = useState(false);
  const [passing, setPassing] = useState(false);
  const [paying, setPaying] = useState(false);

  const loadListing = useCallback(async () => {
    if (!listingId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await bodyListingService.getListing(listingId, {
        vehicleId: resolvedVehicleId,
      });
      setListing(data);
      if (data?.availabilityRequestSent || data?.dispatchPhoto?.url || data?.verificationPhoto?.url) {
        setRequestSent(true);
      }
    } catch (err) {
      setError(err.status === 404 ? 'unavailable' : err.message || 'Failed to load listing');
    } finally {
      setLoading(false);
    }
  }, [listingId, resolvedVehicleId]);

  useEffect(() => {
    loadListing();
  }, [loadListing]);

  useEffect(() => {
    if (!requestSent || listing?.verificationPhoto?.url || listing?.dispatchPhoto?.url) {
      return undefined;
    }
    const id = setInterval(loadListing, 12000);
    return () => clearInterval(id);
  }, [requestSent, listing?.verificationPhoto?.url, listing?.dispatchPhoto?.url, loadListing]);

  const crossSellNodeId = listing?.catalogNodeId || listing?.nodeId;
  const crossSell = useCrossSell(crossSellNodeId, listingId, 12);

  const requireAuth = () => {
    navigate('/login', {
      state: { from: location.pathname + location.search },
    });
    return false;
  };

  const submitCheckAvailability = async () => {
    if (!isAuthenticated && !requireAuth()) return;
    setChecking(true);
    try {
      await bodyListingService.checkAvailability({
        listingId: listing.listingId,
        vehicleId: resolvedVehicleId,
      });
      setShowCheckModal(false);
      setRequestSent(true);
      toast.success('Request sent to seller.');
    } catch (err) {
      toast.error(err.message || 'Failed to send request.');
    } finally {
      setChecking(false);
    }
  };

  const handleAddToCart = async () => {
    if (!listing) return;
    if (!isAuthenticated && !requireAuth()) return;
    setAddingToCart(true);
    try {
      await listingService.addToCart({
        listingId: listing.listingId,
        quantity: 1,
        vehicleId: resolvedVehicleId,
      });
      await refreshCart?.();
      toast.success(`${listing.name} added to cart!`);
    } catch (err) {
      if (err.status === 409) {
        toast.error('This item just went out of stock.');
        return;
      }
      if (err.status === 404) {
        toast.error('This listing is no longer available.');
        return;
      }
      toast.error(err.message || 'Failed to add to cart.');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleApproveDispatch = async () => {
    if (!isAuthenticated && !requireAuth()) return;
    const orderId = listing?.dispatchPhoto?.orderId;
    if (!orderId) {
      toast.error('Dispatch photo order not found.');
      return;
    }
    setApproving(true);
    try {
      const res = await bodyListingService.approveDispatchPhoto({
        orderId,
        listingId: listing.listingId,
      });
      const item = res.checkoutItem || res.data?.checkoutItem;
      const meta = res.bodyPartMeta || res.data?.bodyPartMeta;
      if (!item) throw new Error('Checkout session unavailable');

      localStorage.removeItem('buyNowItem');
      localStorage.setItem(
        'buyNowItem',
        JSON.stringify({
          ...item,
          bodyPartMeta: meta,
        })
      );
      toast.success('Photo approved — proceed to checkout.');
      navigate('/checkout/address');
    } catch (err) {
      toast.error(err.message || 'Approval failed.');
    } finally {
      setApproving(false);
    }
  };

  const handlePassDispatch = async () => {
    toast.info('You passed on this panel. Browse other colour options below.');
    setListing((prev) =>
      prev ? { ...prev, dispatchPhoto: null } : prev
    );
    setPassing(false);
  };

  const handlePayNowAfterPhoto = async () => {
    if (!isAuthenticated && !requireAuth()) return;
    const orderId = listing?.verificationPhoto?.orderId;
    if (!orderId) {
      toast.error('Order not found for this photo.');
      return;
    }
    setPaying(true);
    try {
      const res = await usedListingService.proceedAfterVerificationPhoto({
        orderId,
        listingId: listing.listingId,
      });
      const item = res.checkoutItem || res.data?.checkoutItem;
      const meta = res.usedPartMeta || res.data?.usedPartMeta;
      if (!item) throw new Error('Checkout session unavailable');

      localStorage.removeItem('buyNowItem');
      localStorage.setItem(
        'buyNowItem',
        JSON.stringify({ ...item, usedPartMeta: meta })
      );
      toast.success('Proceed to checkout.');
      navigate('/checkout/address');
    } catch (err) {
      toast.error(err.message || 'Could not proceed to checkout.');
    } finally {
      setPaying(false);
    }
  };

  if (loading) return <ProductDetailSkeleton />;

  if (error === 'unavailable') {
    return (
      <div className={`${sc('pdpShell')} px-4 py-16 text-center`}>
        <p className="text-lg font-semibold">This listing is no longer available.</p>
        <button
          type="button"
          onClick={() => navigate('/catalog')}
          className="mt-6 rounded-xl bg-blue-600 px-6 py-2.5 font-bold text-white"
        >
          Browse alternatives
        </button>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className={`${sc('pdpShell')} flex items-center justify-center`}>
        <p className="text-slate-400">{error || 'Listing not found'}</p>
      </div>
    );
  }

  const gradeMeta = getGradeMeta(listing.grade);
  const showVerificationPhoto = Boolean(listing.verificationPhoto?.url);
  const showDispatchPhoto = listing.dispatchPhoto?.url && listing.dispatchPhoto?.canApprove;
  const hideCheckCta = showDispatchPhoto || showVerificationPhoto;

  const ctaProps = {
    listing,
    requestSent: requestSent && !showDispatchPhoto && !showVerificationPhoto,
    checking,
    addingToCart,
    onCheckAvailability: () => {
      if (!isAuthenticated) {
        requireAuth();
        return;
      }
      setShowCheckModal(true);
    },
    onAddToCart: handleAddToCart,
  };

  return (
    <div className={`${sc('pdpShell')} pb-[calc(8.75rem+env(safe-area-inset-bottom,0px))] md:pb-12`}>
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        <nav className={`mb-5 flex flex-wrap items-center gap-2 text-xs sm:mb-6 sm:text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
          <button
            type="button"
            onClick={() => navigate('/catalog/body')}
            className={`font-medium transition-colors ${isDark ? 'text-amber-400/90 hover:text-amber-300' : 'text-amber-700 hover:text-amber-800'}`}
          >
            Body parts
          </button>
          <span aria-hidden className={isDark ? 'text-slate-600' : 'text-slate-400'}>
            /
          </span>
          <span className={`truncate ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{listing.name}</span>
        </nav>

        <header className={`mb-6 space-y-3 border-b pb-5 sm:mb-8 ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
          <span
            className={`inline-flex rounded-lg border px-3 py-1.5 text-xs font-bold uppercase tracking-wide sm:text-sm ${
              isDark
                ? 'border-amber-500/40 bg-amber-500/15 text-amber-300'
                : 'border-amber-200 bg-amber-50 text-amber-800'
            }`}
          >
            {listing.gradeLabel || gradeMeta.label}
          </span>
          <h1
            className={`text-2xl font-bold leading-tight sm:text-3xl lg:text-[2rem] lg:leading-snug ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
          >
            {listing.name}
          </h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <p className={`font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{listing.brand}</p>
            {listing.partNumber && (
              <p className={`font-mono ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                Part # {listing.partNumber}
              </p>
            )}
          </div>
          {listing.conditionDescription && (
            <p className={`max-w-3xl text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {listing.conditionDescription}
            </p>
          )}
        </header>

        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12 lg:gap-8 xl:gap-10">
          {/* ——— Visual column ——— */}
          <div className="flex flex-col gap-4 lg:col-span-5 lg:sticky lg:top-[4.5rem] lg:self-start">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
              <PanelDamageMap damageZone={listing.damageZone} compact />
              <ColorMatchSection listing={listing} compact />
            </div>
            <UsedPhotoGallery
              photos={listing.photos}
              minRequired={listing.minPhotosRequired || 3}
            />
          </div>

          {/* ——— Buy / details column ——— */}
          <div className="flex w-full min-w-0 flex-col gap-5 lg:col-span-7 lg:gap-6">
            <FitmentStrip fitment={listing.fitment} />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div
                className={`rounded-2xl border p-4 sm:p-5 ${
                  isDark ? 'border-slate-700/60 bg-slate-800/50' : 'border-slate-200 bg-white shadow-sm'
                }`}
              >
                <p className={`text-[11px] font-bold uppercase tracking-[0.15em] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                  Donor vehicle
                </p>
                <p className={`mt-2 text-base font-semibold leading-snug ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {listing.donorVehicle?.label ||
                    [listing.donorVehicle?.make, listing.donorVehicle?.model, listing.donorVehicle?.year]
                      .filter(Boolean)
                      .join(' ')}
                </p>
                {listing.donorVehicle?.odometerKm != null && (
                  <p className={`mt-2 flex items-center gap-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    <FaTachometerAlt className={`shrink-0 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                    {Number(listing.donorVehicle.odometerKm).toLocaleString('en-IN')} km
                  </p>
                )}
              </div>

              <div
                className={`flex items-center gap-3 rounded-2xl border p-4 sm:p-5 ${
                  isDark
                    ? 'border-emerald-800/40 bg-emerald-950/20'
                    : 'border-emerald-200 bg-emerald-50'
                }`}
              >
                <FaShieldAlt className={`shrink-0 text-lg ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                <p className={`text-sm leading-relaxed ${isDark ? 'text-emerald-200' : 'text-emerald-800'}`}>
                  <strong>{listing.warrantyDays || gradeMeta.warrantyDays} days</strong> warranty
                </p>
              </div>
            </div>

            {listing.repaintCostRange?.label && listing.colorMatchType === 'paintable' && (
              <div
                className={`rounded-xl border px-4 py-3 text-sm ${
                  isDark
                    ? 'border-amber-700/40 bg-amber-950/20 text-amber-200'
                    : 'border-amber-200 bg-amber-50 text-amber-800'
                }`}
              >
                Estimated repaint cost: <strong>{listing.repaintCostRange.label}</strong>
              </div>
            )}

            {showVerificationPhoto && (
              <UsedVerificationPhotoSection
                verificationPhoto={listing.verificationPhoto}
                onPayNow={handlePayNowAfterPhoto}
                paying={paying}
              />
            )}

            {showDispatchPhoto && (
              <DispatchPhotoSection
                dispatchPhoto={listing.dispatchPhoto}
                onApprove={handleApproveDispatch}
                onPass={handlePassDispatch}
                approving={approving}
                passing={passing}
              />
            )}

            <div
              className={`rounded-2xl border px-4 py-5 sm:px-5 lg:px-6 ${
                isDark ? 'border-slate-700/60 bg-slate-800/40' : 'border-slate-200 bg-white shadow-sm'
              }`}
            >
              <PriceBreakdownCard pricing={listing.pricing} embedded />
              <SellerBlock
                seller={listing.seller}
                eta={listing.etaLabel || listing.eta}
                warrantyMonths={null}
                embedded
              />
              {!hideCheckCta && (
                <div className="hidden pt-5 md:block">
                  <BodyCtaSection {...ctaProps} />
                </div>
              )}
            </div>

            <BodyAlternativesSection listing={listing} />
          </div>
        </div>

        <div className="mt-8">
          <CrossSellSection items={crossSell} />
        </div>
      </div>

      {showVerificationPhoto ? (
        <div
          className={`fixed inset-x-0 z-[110] border-t px-4 py-3 backdrop-blur-md md:hidden bottom-[calc(3.75rem+env(safe-area-inset-bottom,0px))] shadow-[0_-8px_24px_rgba(15,23,42,0.08)] ${
            isDark ? 'border-slate-700/80 bg-[#0b1120]/95' : 'border-slate-200 bg-white/95'
          }`}
        >
          <button
            type="button"
            onClick={handlePayNowAfterPhoto}
            disabled={paying}
            className="flex w-full items-center justify-center rounded-xl bg-amber-500 px-5 py-3.5 text-base font-bold text-slate-900 disabled:opacity-50"
          >
            {paying ? 'Please wait…' : 'Pay now'}
          </button>
        </div>
      ) : !hideCheckCta ? (
        <div
          className={`fixed inset-x-0 z-[110] border-t px-4 py-3 backdrop-blur-md md:hidden bottom-[calc(3.75rem+env(safe-area-inset-bottom,0px))] shadow-[0_-8px_24px_rgba(15,23,42,0.08)] ${
            isDark ? 'border-slate-700/80 bg-[#0b1120]/95' : 'border-slate-200 bg-white/95'
          }`}
        >
          <BodyCtaSection {...ctaProps} compact />
        </div>
      ) : null}

      {showCheckModal && (
        <ConfirmModal
          title="Check Availability"
          body="Request sent to seller. They will physically check this panel and send you a photo before dispatch. You only pay after approving the photo."
          confirmLabel="Send request"
          loading={checking}
          onConfirm={submitCheckAvailability}
          onCancel={() => setShowCheckModal(false)}
        />
      )}
    </div>
  );
}
